import {IconButton, IssueLabelToken} from '@primer/react'
import type {IssueLabelTokenProps} from '@primer/react'
import {XIcon} from '@primer/octicons-react'
import {parseToRgba, parseToHsla} from 'color2k'
import type React from 'react'
import {useMemo} from 'react'
import {clsx} from 'clsx'

import styles from './LabelToken.module.css'

export interface LabelTokenProps extends IssueLabelTokenProps {
  /**
   * standardize href as a prop on all possible 'as' types, since otherwise we can't destructure it properly
   */
  href?: string

  /**
   * allows the font weight to be overriden by the parent component
   */
  fontWeight?: number

  /**
   * Optional callback to be called when the remove button is clicked.
   */
  onDelete?: () => void

  /**
   * Override the default token interaction styles
   */
  interactive?: boolean
}

const LabelToken = ({
  ref,
  fontWeight,
  onDelete,
  interactive,
  style,
  className,
  fillColor = '#999',
  ...rest
}: LabelTokenProps) => {
  // Primer's IssueLabelToken sets color CSS custom properties via an internal
  // style prop, but spreads {...rest} after it. Any external style prop
  // (including an empty object) overrides those variables and the label
  // falls back to grey. We pre-compute the same CSS vars here and merge
  // them with consumer styles so colors are preserved.
  const mergedStyle = useMemo((): React.CSSProperties => {
    const [r, g, b] = parseToRgba(fillColor)
    const [h, s, l] = parseToHsla(fillColor)
    return {
      '--label-r': String(r),
      '--label-g': String(g),
      '--label-b': String(b),
      '--label-h': String(Math.round(h)),
      '--label-s': String(Math.round(s * 100)),
      '--label-l': String(Math.round(l * 100)),
      ...(fontWeight ? {fontWeight} : {}),
      ...(interactive ? {cursor: 'pointer'} : {}),
      ...style,
    }
  }, [fillColor, fontWeight, interactive, style])

  const token = (
    <IssueLabelToken
      ref={ref}
      style={mergedStyle}
      fillColor={fillColor}
      className={clsx(className, styles.IssueLabelToken)}
      {...rest}
    />
  )

  if (!onDelete) {
    return token
  }

  return (
    <span className={styles.labelTokenBase}>
      {token}
      <IconButton
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          onDelete()
        }}
        variant="invisible"
        icon={XIcon}
        size={'small'}
        aria-label="Remove label"
        className={styles.labelTokenDeleteButton}
      />
    </span>
  )
}

LabelToken.displayName = 'IssueLabelToken'

export {LabelToken}
